import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X, AlertTriangle } from 'lucide-react'
import type { MaterialStockOverviewItem } from '@/services/inventory/types'
import { getGroupDef, formatMaterialAttributes } from '@/domain/materialGroups'
import { stripDiacritics, calculateDropdownPosition, type DropdownPosition } from './Typeahead'

function fmtN(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0'
  const num = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
}

export function formatMaterialDisplay(item?: MaterialStockOverviewItem | null): string {
  if (!item) return ''
  const name = item.name || item.material_name || ''
  const attrs = formatMaterialAttributes(item.group_code, item.attributes)
  if (!attrs) return name
  return `${name} — ${attrs}`
}

export interface MaterialMatchResult {
  material: MaterialStockOverviewItem | null
  ambiguous: boolean
  matches: MaterialStockOverviewItem[]
}

/**
 * Matches pasted text from Excel to a material in the catalog.
 * Supports "Tên vật tư", "Tên — thông số", "Tên [thông số]".
 */
export function matchMaterialFromExcelText(
  rawText: string,
  materials: MaterialStockOverviewItem[],
): MaterialMatchResult {
  const trimmed = (rawText || '').trim()
  if (!trimmed) {
    return { material: null, ambiguous: false, matches: [] }
  }

  const cleanQuery = stripDiacritics(trimmed)
  const tokens = cleanQuery.split(/[\s,·—\-_/x×]+/).filter(Boolean)

  const activeMaterials = materials.filter((m) => m.status !== 'inactive' && m.status !== 'INACTIVE')

  // 1. Exact full display match
  const exactDisplay = activeMaterials.find((m) => {
    const disp = stripDiacritics(formatMaterialDisplay(m))
    return disp === cleanQuery
  })
  if (exactDisplay) {
    return { material: exactDisplay, ambiguous: false, matches: [exactDisplay] }
  }

  // 2. Exact name match
  const nameMatches = activeMaterials.filter((m) => {
    const name = stripDiacritics(m.name || m.material_name || '')
    return name === cleanQuery
  })
  if (nameMatches.length === 1) {
    return { material: nameMatches[0], ambiguous: false, matches: nameMatches }
  }
  if (nameMatches.length > 1) {
    return { material: null, ambiguous: true, matches: nameMatches }
  }

  // 3. Token-based match
  const tokenMatches = activeMaterials.filter((m) => {
    const name = stripDiacritics(m.name || m.material_name || '')
    const attrs = stripDiacritics(formatMaterialAttributes(m.group_code, m.attributes))
    const group = stripDiacritics(getGroupDef(m.group_code).name)
    const combined = `${name} ${attrs} ${group}`
    return tokens.every((tok) => combined.includes(tok))
  })

  if (tokenMatches.length === 1) {
    return { material: tokenMatches[0], ambiguous: false, matches: tokenMatches }
  }
  if (tokenMatches.length > 1) {
    // Check if one tokenMatch has exact name
    const exactNameSubset = tokenMatches.filter((m) => stripDiacritics(m.name || m.material_name || '') === cleanQuery)
    if (exactNameSubset.length === 1) {
      return { material: exactNameSubset[0], ambiguous: false, matches: exactNameSubset }
    }
    return { material: null, ambiguous: true, matches: tokenMatches }
  }

  return { material: null, ambiguous: false, matches: [] }
}

export interface MaterialTypeaheadProps {
  materials: MaterialStockOverviewItem[]
  value: string // material_id
  onChange: (materialId: string, material: MaterialStockOverviewItem | null) => void
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  autoFocus?: boolean
  clearable?: boolean
  showAvailable?: boolean
  className?: string
  inputClassName?: string
  dropdownClassName?: string
  error?: boolean | string
  'aria-label'?: string
}

export function MaterialTypeahead({
  materials,
  value,
  onChange,
  placeholder = 'Tìm hoặc chọn vật tư…',
  disabled = false,
  readOnly = false,
  autoFocus = false,
  clearable = true,
  showAvailable = true,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  error,
  'aria-label': ariaLabel,
}: MaterialTypeaheadProps): React.ReactElement {
  // Current selected item
  const selectedMaterial = useMemo(() => {
    if (!value) return null
    return materials.find((m) => m.material_id === value || m.id === value) || null
  }, [value, materials])

  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState(() => (selectedMaterial ? formatMaterialDisplay(selectedMaterial) : ''))
  const [highlightIndex, setHighlightIndex] = useState(0)
  const [coords, setCoords] = useState<DropdownPosition>({
    left: 0,
    minWidth: 260,
    maxWidth: 480,
    maxHeight: 280,
    flipUp: false,
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Synchronize input text with selected item
  useEffect(() => {
    if (selectedMaterial) {
      setQuery(formatMaterialDisplay(selectedMaterial))
    } else {
      setQuery('')
    }
  }, [selectedMaterial])

  // Filter materials based on search query
  const { filteredItems, totalMatches } = useMemo(() => {
    const activeMaterials = materials.filter((m) => m.status !== 'inactive' && m.status !== 'INACTIVE')
    if (!query.trim()) {
      return { filteredItems: activeMaterials.slice(0, 50), totalMatches: activeMaterials.length }
    }

    const cleanQ = stripDiacritics(query.trim())
    const tokens = cleanQ.split(/\s+/).filter(Boolean)

    const scored: Array<{ item: MaterialStockOverviewItem; score: number }> = []

    for (const m of activeMaterials) {
      const name = stripDiacritics(m.name || m.material_name || '')
      const attrs = stripDiacritics(formatMaterialAttributes(m.group_code, m.attributes))
      const groupName = stripDiacritics(getGroupDef(m.group_code).name)
      const uom = stripDiacritics(m.base_uom || m.uom_default || '')
      const combined = `${name} ${attrs} ${groupName} ${uom}`

      const allTokensMatch = tokens.every((tok) => combined.includes(tok))
      if (!allTokensMatch) continue

      // Higher score for prefix match on name
      let score = 0
      if (name.startsWith(cleanQ)) {
        score = 100
      } else if (name.includes(cleanQ)) {
        score = 50
      } else {
        score = 10
      }

      scored.push({ item: m, score })
    }

    scored.sort((a, b) => b.score - a.score)
    const items = scored.map((s) => s.item)

    return {
      filteredItems: items.slice(0, 50),
      totalMatches: items.length,
    }
  }, [materials, query])

  // Reset highlight index when filtered list changes
  useEffect(() => {
    setHighlightIndex(0)
  }, [filteredItems])

  // Update floating dropdown coordinates
  const updateCoords = useCallback(() => {
    if (!containerRef.current || typeof window === 'undefined') return
    const rect = containerRef.current.getBoundingClientRect()
    const dropdownEl = dropdownRef.current

    const pos = calculateDropdownPosition({
      inputRect: rect,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      dropdownWidth: Math.max(rect.width, 360),
      dropdownHeight: dropdownEl?.offsetHeight || 260,
    })

    if (!pos) {
      setIsOpen(false)
      return
    }

    setCoords(pos)
  }, [])

  useEffect(() => {
    if (isOpen) {
      updateCoords()
    }
  }, [isOpen, filteredItems, updateCoords])

  // Listen to window scroll & resize
  useEffect(() => {
    if (!isOpen) return

    let rafId: number | null = null
    const handleScrollOrResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => updateCoords())
    }

    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpen, updateCoords])

  // Click outside listener
  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const insideContainer = containerRef.current && containerRef.current.contains(target)
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(target)

      if (!insideContainer && !insideDropdown) {
        setIsOpen(false)
        if (selectedMaterial) {
          setQuery(formatMaterialDisplay(selectedMaterial))
        } else {
          setQuery('')
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isOpen, selectedMaterial])

  const handleSelect = (item: MaterialStockOverviewItem) => {
    onChange(item.material_id, item)
    setQuery(formatMaterialDisplay(item))
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange('', null)
    setQuery('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        updateCoords()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : 0))
        break

      case 'ArrowUp':
        e.preventDefault()
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : Math.max(0, filteredItems.length - 1)))
        break

      case 'Enter':
        e.preventDefault()
        if (filteredItems.length > 0 && highlightIndex >= 0 && highlightIndex < filteredItems.length) {
          handleSelect(filteredItems[highlightIndex])
        }
        break

      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        if (selectedMaterial) {
          setQuery(formatMaterialDisplay(selectedMaterial))
        } else {
          setQuery('')
        }
        break

      case 'Tab':
        if (filteredItems.length === 1) {
          handleSelect(filteredItems[0])
        } else {
          setIsOpen(false)
        }
        break
    }
  }

  // Floating portal dropdown
  const dropdownMenu = isOpen && typeof document !== 'undefined' && (
    <div
      ref={dropdownRef}
      role="listbox"
      className={`fixed z-[9999] overflow-auto rounded border border-slate-300 bg-white py-1 shadow-xl text-xs font-sans ${dropdownClassName}`.trim()}
      style={{
        left: coords.left,
        top: coords.top !== undefined ? coords.top : undefined,
        bottom: coords.bottom !== undefined ? coords.bottom : undefined,
        minWidth: Math.max(coords.minWidth, 340),
        maxWidth: 480,
        maxHeight: coords.maxHeight,
      }}
    >
      {filteredItems.length === 0 ? (
        <div className="px-3 py-2 text-xs text-slate-400 italic">
          Không tìm thấy vật tư phù hợp
        </div>
      ) : (
        <>
          {filteredItems.map((item, idx) => {
            const isSelected = selectedMaterial?.material_id === item.material_id
            const isHighlighted = idx === highlightIndex
            const matName = item.name || item.material_name || ''
            const groupDef = getGroupDef(item.group_code)
            const attrStr = formatMaterialAttributes(item.group_code, item.attributes)
            const uomStr = item.base_uom || item.uom_default || ''
            const availableQty = Number(
              item.available_to_reserve ??
                ((item.physical_available ?? item.available ?? 0) - (item.reserved_base ?? 0)),
            )
            const isZeroAvail = showAvailable && availableQty <= 0

            return (
              <div
                key={item.material_id}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`cursor-pointer px-3 py-1.5 border-b border-slate-100 last:border-b-0 transition-colors ${
                  isHighlighted
                    ? 'bg-blue-50 text-blue-950'
                    : isSelected
                      ? 'bg-slate-100 font-medium text-slate-900'
                      : 'text-slate-800 hover:bg-slate-50'
                } ${isZeroAvail ? 'opacity-65' : ''}`}
              >
                {/* Line 1: Tên vật tư */}
                <div className="flex items-center justify-between font-semibold text-[13px] text-slate-900">
                  <span className="truncate">{matName}</span>
                  {isZeroAvail && (
                    <span className="ml-2 inline-flex items-center gap-0.5 text-[10.5px] font-normal text-amber-700 bg-amber-50 px-1 py-0.5 rounded shrink-0">
                      <AlertTriangle className="h-3 w-3" /> Hết khả dụng
                    </span>
                  )}
                </div>

                {/* Line 2: Nhóm · thông số · ĐVT · Có thể dùng N */}
                <div className="mt-0.5 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-slate-600">{groupDef.name}</span>
                  {attrStr && <span>· {attrStr}</span>}
                  {uomStr && <span>· {uomStr}</span>}
                  {showAvailable && (
                    <span
                      className={`font-semibold ${
                        availableQty > 0 ? 'text-emerald-700' : 'text-slate-400'
                      }`}
                    >
                      · Có thể dùng {fmtN(availableQty)}
                    </span>
                  )}
                </div>
              </div>
            )
          })}

          {totalMatches > 50 && (
            <div className="px-3 py-1.5 text-center text-[11px] text-slate-400 bg-slate-50 border-t border-slate-200">
              Còn {totalMatches - 50} kết quả — gõ thêm từ khóa để lọc
            </div>
          )}
        </>
      )}
    </div>
  )

  const isInvalid = Boolean(error)

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex w-full items-center rounded border bg-white transition-colors ${
        isInvalid
          ? 'border-red-500 ring-1 ring-red-500'
          : isOpen
            ? 'border-blue-500 ring-1 ring-blue-500'
            : 'border-slate-300 hover:border-slate-400'
      } ${disabled ? 'bg-slate-50 opacity-60 cursor-not-allowed' : ''} ${className}`.trim()}
    >
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!isOpen) {
            updateCoords()
            setIsOpen(true)
          }
        }}
        onFocus={() => {
          if (!disabled && !readOnly) {
            updateCoords()
            setIsOpen(true)
          }
        }}
        onKeyDown={handleKeyDown}
        className={`w-full min-w-0 bg-transparent px-2.5 py-1.5 text-xs text-slate-800 outline-none placeholder:text-slate-400 font-sans ${inputClassName}`.trim()}
      />

      <div className="flex items-center pr-2 gap-1 shrink-0">
        {clearable && !disabled && !readOnly && value && (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleClear}
            className="text-slate-400 hover:text-slate-600 p-0.5 rounded"
            aria-label="Xóa chọn"
            title="Xóa chọn"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled || readOnly}
          onClick={() => {
            if (!disabled && !readOnly) {
              if (!isOpen) updateCoords()
              setIsOpen((prev) => !prev)
              inputRef.current?.focus()
            }
          }}
          className="text-slate-400 hover:text-slate-600 p-0.5"
          aria-label="Mở danh sách"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {dropdownMenu && createPortal(dropdownMenu, document.body)}
    </div>
  )
}
